import { useState, useEffect, useCallback } from "react";

/**
 * Persistent form state via sessionStorage.
 * Survives a redirect-based OAuth round-trip so users return to a half-filled form
 * with all their inputs intact, ready to click Submit.
 *
 *   const [form, setForm, clearDraft] = useFormDraft("draft_clients", INITIAL);
 *
 * - `key`     — unique sessionStorage key per form
 * - `initial` — default state shape
 */
export default function useFormDraft(key, initial) {
    const [state, setState] = useState(() => {
        try {
            const saved = sessionStorage.getItem(key);
            if (saved) {
                const parsed = JSON.parse(saved);
                // shallow-merge so newly-added fields in `initial` are not lost
                return { ...initial, ...parsed };
            }
        } catch {}
        return initial;
    });

    useEffect(() => {
        try {
            sessionStorage.setItem(key, JSON.stringify(state));
        } catch {}
    }, [key, state]);

    const clearDraft = useCallback(() => {
        try {
            sessionStorage.removeItem(key);
        } catch {}
    }, [key]);

    return [state, setState, clearDraft];
}
